import { useMemo } from "react";
import {
  Box,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { UnitsFormatter } from "hooks/formatters";
import { TFunction } from "i18next";
import {
  CrewCompliance,
  CrewMaterialCompliance,
  DiffMode,
  RecipeComplianceBreakdown,
  Target,
  TargetKind,
} from "src/store/api/generatedApi";
import {
  barPct,
  formatFracDiffPct,
  formatGoalPct,
  formatImpact,
  formatMassTons,
  formatMaterialDiff,
  formatMaterialName,
  formatPva,
  formatScore,
  formatSpecificPrice,
  goalBarPct,
  recipeComplianceColors,
} from "./recipeCompliancePresentation";

import { OverrideCountCell, RenderVariant } from "./OverrideCountCell";
import { OverrideListTable } from "./OverrideListTable";

const barWrapSx: SxProps<Theme> = {
  display: "flex",
  height: 24,
  alignItems: "center",
};
const halfSx: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
};
const centerLineSx: SxProps<Theme> = {
  width: "1px",
  bgcolor: "grey.200",
  alignSelf: "stretch",
  flexShrink: 0,
};

// Optional cadence subline per kind: required-lift trackers read "every heat";
// built-in metrics like layering compliance have none. A kind absent here — or
// one this client doesn't yet know — renders without a subline. Row labels come
// from the target's `name`, or from the kind for built-in (null-name) metrics.
const TARGET_KIND_CADENCE_KEY: Partial<Record<TargetKind, string>> = {
  required_lift: "everyHeat",
};

const StatusDot = ({ color }: { color: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 9,
      height: 9,
      borderRadius: "50%",
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

const GoalBar = ({
  barPercent,
  barColor,
  label,
}: {
  barPercent: number;
  barColor: string | undefined;
  label: string;
}) => (
  <Stack direction="row" alignItems="center" gap={1}>
    <Box
      sx={{
        flex: 1,
        height: 12,
        bgcolor: "grey.100",
        borderRadius: "2px",
        overflow: "hidden",
        minWidth: 80,
      }}
    >
      {barColor && (
        <Box
          sx={{
            height: "100%",
            width: `${barPercent}%`,
            bgcolor: barColor,
            borderRadius: "2px",
          }}
        />
      )}
    </Box>
    <Typography
      variant="body1Mono"
      component="span"
      sx={{ flexShrink: 0, minWidth: 44, textAlign: "right" }}
    >
      {label}
    </Typography>
  </Stack>
);

const DiffBar = ({
  value,
  label,
  widthPct,
}: {
  value: number;
  label: string;
  widthPct: number;
}) => {
  const bar = (
    <Box
      sx={{
        height: 16,
        bgcolor: "grey.700",
        borderRadius: "2px",
        minWidth: "1px",
        width: `${widthPct}%`,
      }}
    />
  );
  const valueLabel = (
    <Typography variant="body1" component="span" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
  );
  if (value === 0) {
    return (
      <Box sx={barWrapSx}>
        <Box sx={{ flex: 1 }} />
        <Box sx={centerLineSx} />
        <Box sx={halfSx}>
          <Typography
            variant="body1"
            component="span"
            sx={{ color: "text.disabled", ml: "3px" }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (value > 0) {
    return (
      <Box sx={barWrapSx}>
        <Box sx={{ flex: 1 }} />
        <Box sx={centerLineSx} />
        <Box sx={{ ...halfSx, gap: "3px" }}>
          {bar}
          {valueLabel}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={barWrapSx}>
      <Box sx={{ ...halfSx, justifyContent: "flex-end", gap: "3px" }}>
        {valueLabel}
        {bar}
      </Box>
      <Box sx={centerLineSx} />
      <Box sx={{ flex: 1 }} />
    </Box>
  );
};

const SectionBannerRow = ({
  label,
  crewCount,
  border,
}: {
  label: string;
  crewCount: number;
  border: string;
}) => {
  const theme = useTheme();
  return (
    <TableRow>
      <TableCell
        colSpan={2}
        sx={{
          ...theme.typography.body2,
          color: "text.secondary",
          textTransform: "uppercase",
          bgcolor: "grey.50",
          borderTop: border,
        }}
      >
        {label}
      </TableCell>
      <TableCell
        colSpan={2 * crewCount}
        sx={{ bgcolor: "grey.50", borderTop: border }}
      />
    </TableRow>
  );
};

export const RecipeComplianceReport = ({
  breakdown,
  diffMode,
  variant,
  t,
  units,
}: {
  breakdown: RecipeComplianceBreakdown;
  diffMode: DiffMode;
  variant: RenderVariant;
  t: TFunction;
  units: UnitsFormatter;
}) => {
  const theme = useTheme();
  const { crews, materials, targets, overrides, override_list } = breakdown;

  const targetLabel = (target: Target): string =>
    target.name ?? t("layeringCompliance");
  const targetFraction = (target: Target, crewId: string): number | null =>
    target.by_crew.find((value) => value.crew_id === crewId)?.fraction ?? null;

  const hairline = `1px solid ${theme.palette.grey[100]}`;
  const colors = recipeComplianceColors({
    green: theme.palette.success.main,
    amber: theme.palette.warning.main,
    red: theme.palette.error.main,
    surface: theme.palette.background.paper,
  });

  const diffValue = (cell: CrewMaterialCompliance): number =>
    diffMode === "mass" ? cell.mass_diff : cell.frac_diff;
  const diffLabel = (cell: CrewMaterialCompliance): string =>
    diffMode === "mass"
      ? formatMaterialDiff(cell.mass_diff)
      : formatFracDiffPct(cell.frac_diff);

  const { maxAbsDiff, maxAbsImpact } = useMemo(() => {
    const diffs = materials.flatMap((material) =>
      material.by_crew.map((cell) =>
        Math.abs(diffMode === "mass" ? cell.mass_diff : cell.frac_diff)
      )
    );
    const impacts = materials.flatMap((material) =>
      material.by_crew.map((cell) => Math.abs(cell.impact))
    );
    return {
      maxAbsDiff: diffs.length ? Math.max(...diffs) : 0,
      maxAbsImpact: impacts.length ? Math.max(...impacts) : 0,
    };
  }, [materials, diffMode]);

  const diffHeader =
    diffMode === "mass"
      ? `${t("diff")}${units("mass")}`
      : `${t("diff")}${units("mass_fraction")}`;

  // Each metric renders as a top-strip "All" KPI (overall) and a per-crew
  // summary footer row (cell). New metrics register here once.
  const summaryMetrics: {
    label: string;
    overall: { text: string; dotColor: string | undefined };
    cell: (crew: CrewCompliance) => {
      text: string;
      dotColor: string | undefined;
    };
  }[] = [
    {
      label: t("netDiff"),
      overall: {
        text: formatMassTons(breakdown.overall.net_diff_mass),
        dotColor: undefined,
      },
      cell: (crew) => ({
        text: formatMassTons(crew.total_mass_diff),
        dotColor: undefined,
      }),
    },
    {
      label: t("verifiedHeats"),
      overall: {
        text: `${breakdown.overall.verified_heats}/${breakdown.overall.total_heats}`,
        dotColor: undefined,
      },
      cell: (crew) => ({
        text: `${crew.num_heats_verified}/${crew.num_heats}`,
        dotColor: undefined,
      }),
    },
    {
      label: t("planVsActual"),
      overall: {
        text: formatPva(breakdown.overall.planned_vs_actual),
        dotColor:
          breakdown.overall.planned_vs_actual !== null
            ? colors.pvaDotColor(breakdown.overall.planned_vs_actual)
            : undefined,
      },
      cell: (crew) => ({
        text: formatPva(crew.planned_vs_actual),
        dotColor:
          crew.planned_vs_actual !== null
            ? colors.pvaDotColor(crew.planned_vs_actual)
            : undefined,
      }),
    },
    {
      label: t("onRecipeScore"),
      overall: {
        text: formatScore(breakdown.overall.on_recipe_score),
        dotColor: colors.complianceDotColor(
          breakdown.overall.on_recipe_score * 100
        ),
      },
      cell: (crew) => ({
        text: formatScore(crew.compliance_score),
        dotColor: colors.complianceDotColor(crew.compliance_score * 100),
      }),
    },
  ];

  const kpis = summaryMetrics.map((metric) => ({
    label: metric.label,
    value: metric.overall.text,
    dotColor: metric.overall.dotColor,
  }));
  const summaryRows = summaryMetrics.map(({ label, cell }) => ({
    label,
    cell,
  }));

  const overrideRows = overrides && [
    ...overrides.by_reason.map((override) => ({
      label:
        [override.reason, override.subreason].filter(Boolean).join(" — ") ||
        "—",
      cells: override.by_crew,
      kind: "reason" as const,
    })),
    {
      label: t("total"),
      kind: "total" as const,
      cells: overrides.total_by_crew,
    },
  ];

  const crewBorder = (crewIndex: number) =>
    crewIndex > 0 ? { borderLeft: hairline } : {};

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        px: 2.25,
        py: 1.5,
      }}
    >
      <Typography variant="h4" align="center" sx={{ mb: 1 }}>
        {breakdown.title}
      </Typography>

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        gap={3}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ mb: 2 }}
      >
        {kpis.map((kpi) => (
          <Stack key={kpi.label} alignItems="center">
            <Stack direction="row" alignItems="center" gap={0.75}>
              {kpi.dotColor && <StatusDot color={kpi.dotColor} />}
              <Typography variant="body1">{kpi.value}</Typography>
            </Stack>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {kpi.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Table
        sx={{
          borderCollapse: "collapse",
          border: hairline,
          width: "max-content",
          minWidth: "100%",
          mx: "auto",
          "& td, & th": {
            padding: "7px 10px",
            textAlign: "center",
            whiteSpace: "nowrap",
            borderBottom: hairline,
          },
          // Row-header (left) column: body text, left-aligned, allowed to wrap.
          // Higher specificity than `& td` so it wins the alignment.
          "& td.rc-row-header": {
            ...theme.typography.body1,
            fontWeight: 600,
            textAlign: "left",
            whiteSpace: "normal",
          },
          // Impact (right) column: higher specificity than `& td` so it beats
          // the table's centre default.
          "& td.rc-impact": {
            textAlign: "right",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ bgcolor: "grey.50" }} />
            <TableCell sx={{ bgcolor: "grey.50" }} />
            {crews.map((crew, crewIndex) => (
              <TableCell
                key={crew.crew_id}
                colSpan={2}
                sx={{
                  bgcolor: "grey.50",
                  borderBottom: "none",
                  ...crewBorder(crewIndex),
                }}
              >
                <Typography variant="body1">{`${t("crew")} ${crew.crew_id}`}</Typography>
                {crew.shift_label && (
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {crew.shift_label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ bgcolor: "grey.50" }} />
            <TableCell sx={{ bgcolor: "grey.50" }}>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {`${t("pricePerTonne")}${units("specific_cost")}`}
              </Typography>
            </TableCell>
            {crews.map((crew, crewIndex) => [
              <TableCell
                key={`${crew.crew_id}-diff`}
                sx={{ bgcolor: "grey.50", ...crewBorder(crewIndex) }}
              >
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {diffHeader}
                </Typography>
              </TableCell>,
              <TableCell
                key={`${crew.crew_id}-impact`}
                sx={{ bgcolor: "grey.50" }}
              >
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  {`${t("impact")}${units("specific_cost")}`}
                </Typography>
              </TableCell>,
            ])}
          </TableRow>
        </TableHead>

        <TableBody>
          {materials.map((material) => (
            <TableRow
              key={material.material_name}
              sx={{
                "&:hover":
                  variant === "interactive" ? { bgcolor: "action.hover" } : {},
              }}
            >
              <TableCell
                className="rc-row-header"
                sx={{
                  pl: "14px",
                  minWidth: 160,
                  maxWidth: 240,
                  bgcolor: "grey.50",
                }}
              >
                {formatMaterialName(material.material_name)}
              </TableCell>
              <TableCell
                sx={{ minWidth: 72, padding: "5px 12px", bgcolor: "grey.50" }}
              >
                <Typography variant="body1">
                  {formatSpecificPrice(material.specific_price ?? null)}
                </Typography>
              </TableCell>
              {material.by_crew.map((cell, crewIndex) => [
                <TableCell
                  key={`${cell.crew_id}-diff`}
                  sx={{
                    minWidth: 160,
                    padding: "5px 9px",
                    ...crewBorder(crewIndex),
                  }}
                >
                  <DiffBar
                    value={diffValue(cell)}
                    label={diffLabel(cell)}
                    widthPct={barPct({
                      val: diffValue(cell),
                      maxAbs: maxAbsDiff,
                    })}
                  />
                </TableCell>,
                <TableCell
                  key={`${cell.crew_id}-impact`}
                  className="rc-impact"
                  sx={{
                    minWidth: 96,
                    padding: "5px 14px 5px 8px",
                    bgcolor: colors.impactColor({
                      val: cell.impact,
                      maxAbs: maxAbsImpact,
                    }),
                  }}
                >
                  <Typography variant="body1">
                    {formatImpact(cell.impact)}
                  </Typography>
                </TableCell>,
              ])}
            </TableRow>
          ))}

          <SectionBannerRow
            label={t("summary")}
            crewCount={crews.length}
            border={hairline}
          />

          {summaryRows.map((summaryRow) => (
            <TableRow key={summaryRow.label}>
              <TableCell
                className="rc-row-header"
                colSpan={2}
                sx={{ pl: "14px", minWidth: 160, bgcolor: "grey.50" }}
              >
                {summaryRow.label}
              </TableCell>
              {crews.map((crew, crewIndex) => {
                const { text, dotColor } = summaryRow.cell(crew);
                return (
                  <TableCell
                    key={crew.crew_id}
                    colSpan={2}
                    sx={{ padding: "8px 10px", ...crewBorder(crewIndex) }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      gap={0.625}
                    >
                      {dotColor && <StatusDot color={dotColor} />}
                      <Typography variant="body1">{text}</Typography>
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}

          {targets.length > 0 && (
            <SectionBannerRow
              label={t("targets")}
              crewCount={crews.length}
              border={hairline}
            />
          )}

          {targets.map((target) => {
            const cadenceKey = TARGET_KIND_CADENCE_KEY[target.kind];
            return (
              <TableRow key={`${target.kind}:${target.name ?? ""}`}>
                <TableCell
                  className="rc-row-header"
                  colSpan={2}
                  sx={{
                    ...theme.typography.body2,
                    fontWeight: "fontWeightMedium",
                    pl: "14px",
                    minWidth: 160,
                    maxWidth: 160,
                    bgcolor: "grey.50",
                  }}
                >
                  {targetLabel(target)}
                  {cadenceKey && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: "fontWeightRegular",
                      }}
                    >
                      {t(cadenceKey)}
                    </Typography>
                  )}
                </TableCell>
                {crews.map((crew, crewIndex) => {
                  const fraction = targetFraction(target, crew.crew_id);
                  return (
                    <TableCell
                      key={crew.crew_id}
                      colSpan={2}
                      sx={{ padding: "8px 12px", ...crewBorder(crewIndex) }}
                    >
                      <GoalBar
                        barPercent={goalBarPct(fraction)}
                        barColor={colors.goalBarColor(fraction)}
                        label={formatGoalPct(fraction)}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}

          {overrideRows && (
            <SectionBannerRow
              label={t("overrides")}
              crewCount={crews.length}
              border={hairline}
            />
          )}
          {overrideRows?.map((overrideRow) => (
            <TableRow key={overrideRow.label}>
              <TableCell
                className="rc-row-header"
                colSpan={2}
                sx={{
                  pl: "14px",
                  minWidth: 160,
                  bgcolor: "grey.50",
                  ...(overrideRow.kind === "reason" && {
                    color: "text.secondary",
                    fontWeight: "fontWeightRegular",
                  }),
                }}
              >
                {overrideRow.label}
              </TableCell>
              {overrideRow.cells.map((cell, crewIndex) => (
                <TableCell
                  key={crews[crewIndex]?.crew_id}
                  colSpan={2}
                  sx={{ padding: "8px 10px", ...crewBorder(crewIndex) }}
                >
                  <OverrideCountCell
                    cell={cell}
                    rowKind={overrideRow.kind}
                    variant={variant}
                    t={t}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {override_list && (
        <OverrideListTable overrideList={override_list} t={t} />
      )}
    </Box>
  );
};
